import styled from "@emotion/styled";
import {
  border,
  borderRadius,
  shadow,
  zIndex,
  fontSize,
  spacing,
  surface,
} from "@atlas-int/tokens";
import {
  generateVisualizationTitle,
  useTitleMetadata,
} from "@atlas-int/data";
import { UIView, VizType } from "@atlas-int/data";
import { VisualizationTitleBase } from "../VizGrid";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCurrentVizType } from "../../routing/typedRoutes";
import { useExploreParams } from "../paramSchemas";
import { useDownload } from "@atlas-int/viz";

const IntraRegionTradeDisclaimer = styled.span`
  && {
    margin-left: ${spacing[2]};
    font-size: ${fontSize.sm};
  }
`;

const TitleTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: 350px;
  font-size: ${fontSize.sm};
  text-transform: none;
  background-color: ${surface.white};
  text-align: center;
  padding: ${spacing[2]};
  box-sizing: border-box;
  z-index: ${zIndex.dropdown};
  border-radius: ${borderRadius.base};
  border: 1px solid ${border.subtle};
  box-shadow: ${shadow.sm};
  white-space: normal;
`;

const TitleWithTooltipContainer = styled.span<{ isTruncated?: boolean }>`
  position: relative;
  display: inline-block;
  cursor: ${({ isTruncated }) => (isTruncated ? "help" : "default")};
  max-width: 300px;
`;

const TitleShortNameContainer = styled.span`
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  font-style: italic;
  vertical-align: text-top;
`;

const VisualizationTitleTooltip = ({ fullName }: { fullName: string }) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const checkTruncation = useCallback(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
  }, [fullName, checkTruncation]);

  return (
    <TitleWithTooltipContainer
      isTruncated={isTruncated}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      <TitleShortNameContainer ref={textRef}>{fullName}</TitleShortNameContainer>
      {isHovered && isTruncated && <TitleTooltip>{fullName}</TitleTooltip>}
    </TitleWithTooltipContainer>
  );
};

const VisualizationTitleGenerator = memo(function VisualizationTitleGenerator({
  queryParams: _queryParams,
}: {
  queryParams?: Record<string, unknown>;
}) {
  const { titleRef } = useDownload();
  const [params] = useExploreParams();
  const {
    view,
    exporter,
    importer,
    year,
    product,
    productClass,
    startYear,
    endYear,
    tradeDirection,
  } = params;
  const servicesClass = (params as Record<string, unknown>).servicesClass as string | undefined;

  const vizType = useCurrentVizType();

  // Resolve location/product names via memoized O(1) lookups
  const {
    exporterName,
    importerName,
    productName: productNameString,
    isWorldExporter,
    isWorldImporter,
    hasIntraRegionTrade: hasIntraRegionTradeRelationship,
  } = useTitleMetadata({ exporter, importer, product, productClass, servicesClass });

  // Generate title string using the unified dispatcher
  const title = useMemo(
    () =>
      generateVisualizationTitle({
        vizType,
        view,
        exporter,
        importer,
        exporterName,
        importerName,
        productNameString,
        year,
        startYear,
        endYear,
        tradeDirection,
        isWorldExporter,
        isWorldImporter,
      }),
    [
      vizType,
      view,
      exporter,
      importer,
      exporterName,
      importerName,
      productNameString,
      year,
      startYear,
      endYear,
      tradeDirection,
      isWorldExporter,
      isWorldImporter,
    ],
  );

  // Parse title template into JSX parts (product names get tooltip treatment)
  const { titleParts, titleString } = useMemo(() => {
    if (!title) return { titleParts: [] as (string | React.ReactElement)[], titleString: "" };

    const parts: (string | React.ReactElement)[] = [];
    let str = "";

    const rawParts = title.split(/({{.*?}})/);
    rawParts.forEach((part, i) => {
      if (!part || part === "undefined") return;

      if (part.startsWith("{{") && part.endsWith("}}")) {
        const productName = part.slice(2, -2);
        parts.push(
          <VisualizationTitleTooltip key={i} fullName={productName} />,
        );
        str += productName;
      } else {
        parts.push(part);
        str += part;
      }
    });

    return { titleParts: parts, titleString: str };
  }, [title]);

  useEffect(() => {
    if (titleString) {
      titleRef.current = titleString;
    }
  }, [titleString, titleRef]);

  if (titleParts.length === 0) return null;

  return (
    <VisualizationTitleBase>
      {titleParts}
      {hasIntraRegionTradeRelationship && (
        <IntraRegionTradeDisclaimer>
          (Includes intra-region trade)
        </IntraRegionTradeDisclaimer>
      )}
    </VisualizationTitleBase>
  );
});

export default VisualizationTitleGenerator;
